import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

const dotVariants = cva(
	"relative aspect-square rounded-full border bg-surface-base flex items-center justify-center flex-shrink-0",
	{
		variants: {
			size: {
				sm: "h-3 w-3",
				md: "h-4 w-4",
			},
			state: {
				default: "border-bc-stronger",
				disabled: "border-bc-stronger opacity-40",
				readonly: "border-bc-stronger",
			},
		},
		defaultVariants: { size: "md", state: "default" },
	},
);

// Inner accent ring rendered only when checked; thick border leaves the classic radio center hole.
const fillVariants = cva("absolute top-[-1px] left-[-1px] aspect-square rounded-full", {
	variants: {
		size: {
			sm: "h-3 w-3 border-[5px]",
			md: "h-4 w-4 border-[6px]",
		},
		state: {
			default: "border-brand-accent",
			disabled: "border-brand-accent opacity-60",
			readonly: "border-bc-stronger",
		},
	},
	defaultVariants: { size: "md", state: "default" },
});

type DotVariantProps = VariantProps<typeof dotVariants>;

export type SelectionDotProps = HTMLAttributes<HTMLDivElement> &
	DotVariantProps & {
		/** Whether the indicator renders its filled (selected) state. */
		checked: boolean;
		/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
		trackingId?: string;
		ref?: Ref<HTMLDivElement>;
	};

export const SelectionDot: FC<SelectionDotProps> = ({
	checked,
	size,
	state,
	trackingId,
	className,
	ref,
	...rest
}) => (
	<div
		ref={ref}
		className={cn(
			dotVariants({ size, state }),
			state === "readonly" && !checked ? "border-opacity-40" : undefined,
			className,
		)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{checked && <div className={fillVariants({ size, state })} />}
	</div>
);
SelectionDot.displayName = "SelectionDot";

export type SelectionDotState = NonNullable<DotVariantProps["state"]>;
export type SelectionDotSize = NonNullable<DotVariantProps["size"]>;
